import { ArtifactType } from "../types/artifact";
import { logError } from "./error";

/**
 * Interface representing the structure for JSON export
 */
export interface JsonExportItem {
  interaction_id: string;
  summary?: string;
  transcript?: any;
  call_data_record?: any;
  scorecard?: any;
}

/**
 * Converts a collection of artifact data into JSON format
 * @param artifacts The artifacts data returned from fetchArtifactsForRag
 * @returns A JSON string representing an array of interaction data
 */
export const convertToJson = (artifacts: any[]): string => {
  if (!artifacts || artifacts.length === 0) {
    return "[]";
  }

  const jsonItems: JsonExportItem[] = artifacts.map(artifact => {
    // Create the base item with interaction_id
    const item: JsonExportItem = {
      interaction_id: artifact.interactionId
    };

    // Extract summary if available
    item.summary = artifact[ArtifactType.Summary] || "";

    // Extract transcript if available
    item.transcript = artifact[ArtifactType.Transcript] || {};

    // Extract call data record if available (checking all possible call data types)
    const callDataTypes = [
      ArtifactType.CallDataRecord,
      ArtifactType.CallRecordingEvent,
      ArtifactType.CallTranscriptionRecord
    ];

    for (const type of callDataTypes) {
      if (artifact[type]) {
        item.call_data_record = artifact[type];
        break; // Only take the first one we find
      }
    }

    // Extract scorecard if available
    item.scorecard = artifact[ArtifactType.Scorecard] || {};

    return item;
  });

  // Convert the entire array to a JSON string
  return JSON.stringify(jsonItems, null, 2);
};

/**
 * Copies JSON data to the clipboard
 * @param jsonData JSON formatted string to copy
 * @returns Promise that resolves when the copy operation is complete
 */
export const copyJsonToClipboard = async (jsonData: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(jsonData);
    return true;
  } catch (error) {
    logError("Failed to copy to clipboard", error);
    return false;
  }
};
